import { ChangeEvent, useEffect, useMemo } from 'react';
import {
  CustomShippingResponseDto,
  LocationResponseDto,
} from '@tiendanube/common';
import { Stack } from 'commons/components';
import { useGetProvinces } from 'domains/Shipping/Addresses/hooks';
import { SelectedZonesInterface } from 'domains/Shipping/components';
import Skeleton from './Skeleton';
import {
  CustomShippingCostCard,
  CustomShippingNameCard,
  ShippingConditionsCard,
  ShippingLocationCard,
  ShippingTermCard,
} from '../../../components';
import CustomShippingRangeOptionsCard from '../../../components/CustomShippingRangeOptionsCard';

interface CustomShippingFormProps {
  values: CustomShippingResponseDto;
  locations: LocationResponseDto[];
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  setFieldValue: (name: string, value: any) => void;
  fetchLocations: () => Promise<void>;
  errors?: Partial<Record<string, string>>;
}

export type DeliveryCostType = 'with-cost' | 'free' | 'to-be-arranged';

function CustomShippingForm({
  values,
  locations,
  handleChange,
  setFieldValue,
  fetchLocations,
  errors,
}: CustomShippingFormProps) {
  const { provinces } = useGetProvinces();

  const selectedZones = useMemo(
    (): SelectedZonesInterface[] =>
      values.deliveryZones?.map((zone) => {
        const province = provinces?.find((p) => p.id === zone.provinceId);
        return {
          provinceId: zone.provinceId,
          provinceCode: province?.code || '',
          provinceName: zone.provinceName,
          cities: zone.cities,
        };
      }) || [],
    [values.deliveryZones, provinces],
  );

  useEffect(() => {
    if (!locations.length) {
      fetchLocations();
    }
    if (locations.length === 1 && !values.locationId) {
      setFieldValue('locationId', locations[0]?.id || '');
    }
  }, [fetchLocations, setFieldValue, locations, values]);

  return (
    <Stack column>
      <CustomShippingNameCard
        name={values.name}
        deliveryCost={values.deliveryCost}
        allowedForFreeShipping={values.allowedForFreeShipping}
        onChange={handleChange}
        error={errors?.name}
      />

      {locations.length > 1 && (
        <ShippingLocationCard
          locations={locations}
          locationId={values.locationId}
          error={errors && errors.locationId}
          onChange={handleChange}
        />
      )}

      <CustomShippingRangeOptionsCard
        rangeType={values.rangeType}
        ranges={values.ranges}
        selectedZones={selectedZones}
        setFieldValue={setFieldValue}
      />

      {values.rangeType !== 'ranges' && (
        <CustomShippingCostCard
          cost={values.cost}
          deliveryCost={values.deliveryCost}
          onChange={handleChange}
        />
      )}

      {values.rangeType !== 'ranges' && (
        <ShippingTermCard
          timeMin={values.deliveryTimeMin}
          timeMax={values.deliveryTimeMax}
          onChange={handleChange}
          error={errors}
        />
      )}

      <ShippingConditionsCard
        onChange={handleChange}
        weightMin={values.cartWeightMin}
        weightMax={values.cartWeightMax}
        valueMin={values.cartValueMin}
        valueMax={values.cartValueMax}
        error={errors}
      />
    </Stack>
  );
}

CustomShippingForm.Skeleton = Skeleton;

export default CustomShippingForm;
